import React, { useEffect, useState } from "react";
import BookingSummary from "../booking-summary";
import ChatContainer from "./chat-container";
import { toast } from "react-toastify";
import handleServerError, { handleUnauthorizedError } from "../../../api/helpers/handleErrorResponses.util";
import { useNavigate, useSearchParams } from "react-router-dom";
import { singleChatApi, updateAllMessageStatusApi } from "../../../api/dashboardApi";
import { updateUnreadMessagesCout } from "../../../redux/actions/profile/profileActions";
import { useDispatch } from "react-redux";
import moment from "moment";
import _ from "lodash";
import toggleClass from "../../../utils/dom/toggle-class.util";

const FETCH_OFFSET = 150;

function SeletectedChat() {
  const [selectedChat, setSelectedChat] = useState({
    bookingDetails: {},
  });

  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();

  const bookingId = Number(searchParams.get("bookingId")) || null;
  const { bookingDetails } = selectedChat;

  const loadSingleChatData = async (chatBodyElm = null, isInitial = false, payload = null) => {
    if (isInitial) {
      setIsInitialLoading(true);
      chatBodyElm.setAttribute("data-all-old-messages-loaded", true);

      const chatDayContainers = chatBodyElm.querySelectorAll("div.chat-day-container:not(.chat-day-container-skeleton)");

      chatDayContainers.forEach((container) => {
        container.remove();
      });
    }

    chatBodyElm.setAttribute("data-is-updating", true);

    if (!payload) {
      payload = {
        bookingId,
        includeBookingDetails: isInitial,
        pageSize: 15,
      };
    }

    singleChatApi(payload)
      .then((res) => {
        const { bookingDetails, messages, pagination, meta } = res.data || {};

        if (isInitial) {
          setSelectedChat({
            bookingDetails,
          });
        }

        if (!messages) return;

        const groupedMessages = getGroupedMessages(messages);

        const { cursor, itemsPerPage } = pagination;

        const hasNextPage = cursor.hasNextPage || false;
        const nextCursorId = cursor.next?.id || null;

        if (hasNextPage) {
          chatBodyElm.setAttribute("data-all-old-messages-loaded", false);
        } else {
          chatBodyElm.setAttribute("data-all-old-messages-loaded", true);
          chatBodyElm.removeEventListener("scroll", debouncedHandleScroll);
        }

        chatBodyElm.setAttribute("data-is-updating", false);

        chatBodyElm.setAttribute("data-next-cursor-id", nextCursorId);
        chatBodyElm.setAttribute("data-has-next-page", hasNextPage);
        chatBodyElm.setAttribute("data-items-per-page", itemsPerPage);

        const scrollTopBefore = chatBodyElm.scrollTop;
        const scrollHeightBefore = chatBodyElm.scrollHeight;

        let prevGroupDate = null;

        groupedMessages.forEach((group) => {
          let isPrepend = true;
          const { date } = group;

          if (prevGroupDate) {
            const prevDate = new Date(prevGroupDate);
            const currentDate = new Date(date);

            if (prevDate < currentDate) {
              isPrepend = false;
            }
          }

          prevGroupDate = date;

          updateChatDay(group, isPrepend);
        });

        if (isInitial) {
          chatBodyElm.scrollTop = chatBodyElm.scrollHeight;
        } else {
          // Measure the new height of the container after adding new messages
          const scrollHeightAfter = chatBodyElm.scrollHeight;

          // Restore the scroll position to account for the newly added elements
          chatBodyElm.scrollTop = scrollTopBefore + (scrollHeightAfter - scrollHeightBefore);
        }

        if (meta.totalUnseenCount > 0) {
          updateAllMessageStatusApi({ bookingId })
            .then((res) => {
              dispatch(
                updateUnreadMessagesCout({
                  count: res.data.totalUnseenCount,
                })
              );
            })
            .catch((err) => {
              if (err.status === 401) {
                return handleUnauthorizedError(err);
              }
            });
        }
      })
      .catch((err) => {
        chatBodyElm.setAttribute("data-loading-error", true);
        chatBodyElm.setAttribute("data-is-updating", false);
        chatBodyElm.setAttribute("data-all-old-messages-loaded", true);
        chatBodyElm.setAttribute("data-next-cursor-id", null);
        chatBodyElm.removeEventListener("scroll", debouncedHandleScroll);
        chatBodyElm.setAttribute("data-has-next-page", false);

        if (err.status >= 500) {
          return handleServerError(err);
        }
        if (err.status === 401) {
          return handleUnauthorizedError(err);
        }
        if (err.code === "REQUEST_PROCESSING_ERROR") {
          toast.error(err.message);
          navigate("/dashboard/messages");
        }
      })
      .finally(() => {
        if (isInitial) {
          setIsInitialLoading(false);
        }
      });
  };

  async function handleScroll(e) {
    const chatContainer = e.target;

    // get current page , total pages and items per page from chatContainer data-attribute
    const hasNextPage = Number(chatContainer.getAttribute("data-has-next-page")) || 1;
    const itemsPerPage = Number(chatContainer.getAttribute("data-items-per-page")) || 15;

    // get is udateing value from chatContainer data-attribute
    const isUpdating = chatContainer.getAttribute("data-is-updating") === "true";

    const scrollTop = chatContainer.scrollTop;

    if (scrollTop > FETCH_OFFSET || !hasNextPage || isUpdating) {
      return;
    }

    chatContainer.setAttribute("data-is-updating", true);

    const payload = {
      bookingId,
      includeBookingDetails: false,
      pageSize: itemsPerPage,
      cursorId: chatContainer.getAttribute("data-next-cursor-id"),
    };

    loadSingleChatData(chatContainer, false, payload);
  }

  const debouncedHandleScroll = _.debounce(handleScroll, 100);

  useEffect(() => {
    setSelectedChat({
      bookingDetails: {},
    });

    if (!bookingId) {
      setIsInitialLoading(false);
      return;
    }

    const chatBodyElm = document.getElementById("chat-body");

    loadSingleChatData(chatBodyElm, true);

    chatBodyElm.addEventListener("scroll", debouncedHandleScroll);

    return () => {
      chatBodyElm.removeEventListener("scroll", debouncedHandleScroll);
    };
  }, [bookingId]);

  if (!bookingId) {
    return null;
  }

  // canot send message to the past reservations
  const isPastReservation = bookingDetails?.reservationPhase === "PAST";

  const isDisabledChat = bookingDetails?.status !== "ACTIVE" || isPastReservation;

  return (
    <>
      <ChatContainer selectedChat={selectedChat} isDisabledChat={isDisabledChat} isInitialLoading={isInitialLoading} />

      <aside aria-label="Booking details">
        <BookingSummary singleReservation={bookingDetails} loading={isInitialLoading} />
      </aside>
    </>
  );
}

export default SeletectedChat;

// Create chat day container
function getChatDayContainer(date) {
  // get the element if it exists
  const existingContainer = document.querySelector(`.chat-container .chat-day-container[data-date="${date}"]`);

  if (existingContainer) {
    return {
      chatDayContainer: existingContainer,
      isExisting: true,
    };
  }

  const chatDayContainer = document.createElement("div");
  toggleClass(chatDayContainer, "chat-day-container", true);
  chatDayContainer.setAttribute("data-date", date);

  const title = document.createElement("div");
  toggleClass(title, "chat-day-title", true);
  title.innerText = date;

  const hr = document.createElement("div");
  toggleClass(hr, "chat-day-title-hr", true);

  chatDayContainer.appendChild(title);
  chatDayContainer.appendChild(hr);

  return {
    chatDayContainer,
    isExisting: false,
  };
}

function getChatDayBody(container) {
  if (!container) return;

  const existingBody = container.querySelector(".chat-day-body");

  if (existingBody) {
    return existingBody;
  }

  const body = document.createElement("div");
  toggleClass(body, "chat-day-body", true);

  container.appendChild(body);

  return body;
}

const doubleCheckSvg = `
  <svg width="800px" height="800px" viewBox="0 0 24 24" fill="none" class="chat-msg-status" xmlns="http://www.w3.org/2000/svg">
    <path d="M1.5 12.5L5.57574 16.5757C5.81005 16.8101 6.18995 16.8101 6.42426 16.5757L9 14" stroke="#000000" stroke-width="1.5" stroke-linecap="round"/>
    <path d="M16 7L12 11" stroke="#000000" stroke-width="1.5" stroke-linecap="round"/>
    <path d="M7 12L11.5757 16.5757C11.8101 16.8101 12.1899 16.8101 12.4243 16.5757L22 7" stroke="#000000" stroke-width="1.5" stroke-linecap="round"/>
  </svg>
`;

export function createChatMessage(messageDetails) {
  let { id, message, userType, isSeen = false, createdAt } = messageDetails;
  userType = userType === "GUEST_USER" ? "chat-guest-msg" : "chat-host-msg";
  const time = moment.utc(createdAt).local().format("h:mm A") || "";

  const msgContainer = document.createElement("div");
  msgContainer.setAttribute("data-message-id", id);
  msgContainer.setAttribute("data-is-seen", isSeen);

  toggleClass(msgContainer, "chat-msg", true);
  toggleClass(msgContainer, userType, true);

  const msgText = document.createElement("p");
  toggleClass(msgText, "chat-msg-text", true);
  toggleClass(msgText, "user-input-viewer", true);
  msgText.innerText = message;

  msgContainer.appendChild(msgText);

  const msgMeta = document.createElement("div");
  toggleClass(msgMeta, "chat-msg-meta", true);

  const msgTime = document.createElement("div");
  toggleClass(msgTime, "chat-msg-time", true);
  msgTime.innerText = time;

  msgMeta.appendChild(msgTime);

  if (userType === "chat-guest-msg") {
    msgMeta.innerHTML += doubleCheckSvg;
  }

  msgContainer.appendChild(msgMeta);

  return msgContainer;
}

function updateChatDay(group, prepend = false) {
  const { date, msgs: messages } = group;

  const messagesContainer = document.getElementById("messages-container");

  const { chatDayContainer, isExisting = false } = getChatDayContainer(date);

  const messageFragment = document.createDocumentFragment();

  messages.forEach((message) => {
    const messageElement = createChatMessage(message);
    messageFragment.appendChild(messageElement);
  });

  const chatDayBody = getChatDayBody(chatDayContainer);

  if (prepend) {
    chatDayBody.prepend(messageFragment);

    if (!isExisting) {
      messagesContainer.prepend(chatDayContainer);
    }
  } else {
    chatDayBody.appendChild(messageFragment);
    if (!isExisting) {
      messagesContainer.appendChild(chatDayContainer);
    }
  }
}

function getGroupedMessages(messages) {
  const groupedMessages = [];
  const dateMap = {};

  messages.forEach((message) => {
    const dateOptions = { year: "numeric", month: "2-digit", day: "2-digit" };
    const date = new Date(message.createdAt).toLocaleDateString("en-US", dateOptions);

    if (!dateMap[date]) {
      dateMap[date] = {
        date: date,
        msgs: [],
      };
      groupedMessages.push(dateMap[date]);
    }

    dateMap[date].msgs.push(message);
  });

  return groupedMessages;
}
